import { OdataParams, OdataService } from '@methylium/odata';
import { DevicesService, Device as MdmDevice } from '@rocksoft/mdm-sdk-v2';
import { Device } from '@rocksoft/shared';
import { IDatasource, IGetRowsParams } from 'ag-grid-community';
import { Observable, Subscription } from 'rxjs';
import { filter, map } from 'rxjs/operators';

export interface DeviceUpdateResult {
  device: Device;
  shouldRefreshGrid: boolean;
}

export class DevicesOverviewDatasource implements IDatasource {
  public currentOdataModel?: OdataParams;

  private datasource: Device[] = [];
  private readonly subscriptions: Subscription[] = [];

  constructor(
    private readonly api: DevicesService,
    private readonly odataService: OdataService
  ) {}

  getRows(params: IGetRowsParams): void {
    const newOdataModel: OdataParams = {
      top: params.endRow - params.startRow,
      skip: params.startRow,
      filter: this.odataService.generateFilterStringForApi(params.filterModel) || undefined,
      orderby: params.sortModel?.length ? this.odataService.generateOrderbyString(params.sortModel) : undefined,
    };

    if (this.shouldResetData(newOdataModel)) {
      this.datasource = [];
    }

    this.currentOdataModel = newOdataModel;
    let lastRow = -1;

    if (this.shouldFetchData(params)) {
      // Item range not available in datasource. Fetch it from the API

      const devicesSub = this.getDevices(newOdataModel)
        .pipe(filter((devices: Device[]) => devices !== null))
        .subscribe((devices: Device[]) => {
          this.datasource = this.datasource.concat(devices);

          lastRow = this.datasource.length < params.endRow ? this.datasource.length : -1;

          params.successCallback(devices, lastRow);
        });

      this.subscriptions.push(devicesSub);
    } else {
      // Item range available in datasource

      lastRow = this.datasource.length < params.endRow ? this.datasource.length : -1;
      const currentData = this.datasource.slice(params.startRow, params.endRow);

      params.successCallback(currentData, lastRow);
    }
  }

  private getDevices(odataParams: OdataParams): Observable<Device[]> {
    return this.api
      .getDevices({
        apiVersion: '2.0',
        $orderby: odataParams.orderby,
        $filter: odataParams.filter,
        $top: odataParams.top,
        $skip: odataParams.skip,
        $skiptoken: odataParams.skiptoken,
        $select: odataParams.select,
        $expand: odataParams.expand,
      })
      .pipe(map((devices: MdmDevice[]) => devices.map((device) => this.mapDevice(device))));
  }

  public updateDevice(device: MdmDevice): DeviceUpdateResult | undefined {
    const deviceIndex = this.datasource.findIndex((cachedDevice) => cachedDevice.udid === device.identifier);

    if (deviceIndex === -1) {
      return undefined;
    }

    const previousDevice = this.datasource[deviceIndex];
    const updatedDevice = this.mapDevice(device);
    this.datasource[deviceIndex] = updatedDevice;

    return {
      device: updatedDevice,
      shouldRefreshGrid:
        previousDevice.serialNumber !== updatedDevice.serialNumber &&
        (this.currentOdataModel?.filter !== undefined ||
          this.currentOdataModel?.orderby?.includes('serialNumber') === true),
    };
  }

  private mapDevice(device: MdmDevice): Device {
    return {
      udid: device.identifier,
      name: device.displayName,
      serialNumber: device.serialNumber,
      lastSeen: device.lastSeen,
      platform: device.type,
      hasLoadedMdmDevice: true,
    };
  }

  private shouldFetchData(params: IGetRowsParams): boolean {
    return this.datasource.length <= params.endRow && this.datasource.length <= params.startRow;
  }

  private shouldResetData(newOdataModel: OdataParams) {
    return (
      this.currentOdataModel?.filter !== newOdataModel.filter ||
      this.currentOdataModel?.orderby !== newOdataModel.orderby
    );
  }

  refresh(): void {
    this.subscriptions.forEach((subscription) => {
      subscription.unsubscribe();
    });
    this.subscriptions.length = 0;
    this.currentOdataModel = undefined;
    this.datasource = [];
  }

  destroy() {
    this.refresh();
  }
}
